import React, { useState, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, PanResponder, GestureResponderEvent, PanResponderGestureState } from 'react-native';
import Svg, { Line, Circle, Text as SvgText } from 'react-native-svg';
import { VisualData } from '@eduwrite/shared';

interface Props {
    data: Extract<VisualData, { type: 'number_line' }>;
    context: Record<string, number | string>;
    onInput: (input: string) => void;
}

export const NumberLineVisual: React.FC<Props> = ({ data, context: _context, onInput }) => {
    const min = data.min;
    const max = data.max;
    const tickInterval = data.tickInterval;
    const [jumps, setJumps] = useState<{ from: number; to: number; label?: string }[]>(data.jumps || []);
    const [dragX, setDragX] = useState<number | null>(null);

    const ticks: number[] = [];
    for (let i = min; i <= max; i += tickInterval) {
        ticks.push(i);
    }
    const totalTicks = ticks.length;

    const tickToX = useCallback((tickIndex: number) => {
        return 20 + (tickIndex / (totalTicks - 1)) * 360;
    }, [totalTicks]);

    const xToNearestTick = useCallback((x: number) => {
        const clamped = Math.max(20, Math.min(380, x));
        const ratio = (clamped - 20) / 360;
        const idx = Math.round(ratio * (totalTicks - 1));
        return Math.max(0, Math.min(totalTicks - 1, idx));
    }, [totalTicks]);

    const panResponder = useRef(
        PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onMoveShouldSetPanResponder: () => true,
            onPanResponderGrant: (evt: GestureResponderEvent) => {
                const touch = evt.nativeEvent.touches[0];
                if (touch) {
                    setDragX(touch.pageX);
                }
            },
            onPanResponderMove: (evt: GestureResponderEvent, gestureState: PanResponderGestureState) => {
                if (dragX !== null) {
                    // dragX is pageX; we approximate SVG x by using gestureState.moveX
                    setDragX(gestureState.moveX);
                }
            },
            onPanResponderRelease: (evt: GestureResponderEvent, gestureState: PanResponderGestureState) => {
                if (dragX !== null) {
                    // Approximate SVG x from moveX (relative to screen layout)
                    // We use a simple heuristic: map moveX to SVG 20..380 range via the container width
                    const svgX = (gestureState.moveX / 400) * 360 + 20; // rough mapping based on viewBox
                    const tickIdx = xToNearestTick(svgX);
                    const toTick = ticks[tickIdx];
                    const lastJump = jumps.length > 0 ? jumps[jumps.length - 1].to : min;
                    if (toTick > lastJump) {
                        const nextJump = { from: lastJump, to: toTick };
                        const updated = [...jumps, nextJump];
                        setJumps(updated);
                        onInput(JSON.stringify(updated));
                    }
                    setDragX(null);
                }
            },
            onPanResponderTerminate: () => {
                setDragX(null);
            },
        })
    ).current;

    const handleAddJump = () => {
        const lastJump = jumps.length > 0 ? jumps[jumps.length - 1].to : min;
        const nextJump = { from: lastJump, to: lastJump + tickInterval };
        const updated = [...jumps, nextJump];
        setJumps(updated);
        onInput(JSON.stringify(updated));
    };

    // Compute drag tick index for preview
    const dragTickIdx = dragX !== null ? xToNearestTick((dragX / 400) * 360 + 20) : null;

    return (
        <View style={styles.container}>
            <View style={styles.svgContainer}>
                <Svg height={140} width="100%" viewBox="0 0 400 140">
                    {/* Main line */}
                    <Line x1="20" y1="80" x2="380" y2="80" stroke="#1E293B" strokeWidth={3} />

                    {/* Ticks */}
                    {ticks.map((t, i) => {
                        const x = tickToX(i);
                        return (
                            <React.Fragment key={t}>
                                <Line x1={x} y1="70" x2={x} y2="90" stroke="#1E293B" strokeWidth={2} />
                                <SvgText x={x} y="110" fontSize="14" fontWeight="600" textAnchor="middle" fill="#1E293B">
                                    {t}
                                </SvgText>
                            </React.Fragment>
                        );
                    })}

                    {/* Jumps */}
                    {jumps.map((jump, idx) => {
                        const fromIdx = ticks.indexOf(jump.from);
                        const toIdx = ticks.indexOf(jump.to);
                        if (fromIdx < 0 || toIdx < 0) return null;
                        const x1 = tickToX(fromIdx);
                        const x2 = tickToX(toIdx);
                        const midX = (x1 + x2) / 2;
                        return (
                            <React.Fragment key={idx}>
                                <Line x1={x1} y1="60" x2={x2} y2="60" stroke="#3B82F6" strokeWidth={3} strokeDasharray="4,4" />
                                <Circle cx={midX} cy="55" r={14} fill="#3B82F6" />
                                <SvgText x={midX} y="60" fontSize="12" fontWeight="700" textAnchor="middle" fill="#FFFFFF">
                                    {jump.label || `+${jump.to - jump.from}`}
                                </SvgText>
                            </React.Fragment>
                        );
                    })}

                    {/* Drag preview */}
                    {dragTickIdx !== null && (
                        <React.Fragment>
                            <Circle cx={tickToX(dragTickIdx)} cy="55" r={16} fill="#F59E0B" opacity={0.6} />
                            <SvgText x={tickToX(dragTickIdx)} y="60" fontSize="12" fontWeight="700" textAnchor="middle" fill="#FFFFFF">
                                {ticks[dragTickIdx]}
                            </SvgText>
                        </React.Fragment>
                    )}
                </Svg>
            </View>

            {/* Draggable handle — 48pt touch target */}
            <View style={styles.handleRow} {...panResponder.panHandlers}
                accessibilityRole="button"
                accessibilityLabel="Drag handle"
                accessibilityHint="Drag to create a jump on the number line"
            >
                <View style={styles.handle}>
                    <Text style={styles.handleText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>↔</Text>
                </View>
                <Text style={styles.handleLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Drag to jump</Text>
            </View>

            <TouchableOpacity style={styles.jumpButton} onPress={handleAddJump}
                accessibilityRole="button"
                accessibilityLabel={`Add jump of ${tickInterval}`}
                accessibilityHint="Adds a jump to the number line"
            >
                <Text style={styles.jumpButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Add jump of {tickInterval}</Text>
            </TouchableOpacity>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        padding: 8,
    },
    svgContainer: {
        width: '100%',
        height: 120,
    },
    jumpButton: {
        marginTop: 12,
        backgroundColor: '#3B82F6',
        borderRadius: 12,
        paddingHorizontal: 20,
        paddingVertical: 10,
    },
    jumpButtonText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#FFFFFF',
    },
    handleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 8,
        paddingVertical: 8,
        paddingHorizontal: 16,
        gap: 12,
    },
    handle: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: '#F59E0B',
        alignItems: 'center',
        justifyContent: 'center',
    },
    handleText: {
        fontSize: 20,
        color: '#FFFFFF',
    },
    handleLabel: {
        fontSize: 14,
        fontWeight: '600',
        color: '#64748B',
    },
});
